import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box, Grid, Chip, Backdrop, CircularProgress, Tooltip} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import DefaultLayout from "layout/defaultLayout";
import PageTitles from "../../components/PageTitles";
import {DatePicker} from "../../components/DatePicker";
import MetricsComponent from "../../features/new-homepage/MetricsComponent";
import ReportingMetricsTable from "../../features/new-homepage/ReportingMetricsTable";
import ChartComponent from "../../features/new-homepage/ChartComponent";
import DeviceComponent from "../../features/new-homepage/DeviceComponent";
import TopHotelsComponent from "../../features/new-homepage/TopHotelsComponent";
import TopMarketsComponent from "../../features/new-homepage/TopMarketsComponent";
import TableContainer from "@mui/material/TableContainer";

import {useFetchMetricsData} from "./utils/hooks/useFetchMetricsData";

import "./new-homepage.scss";
import HotelsFilterMenu from "components/hotelFilterMenu";
import { updateGroupId, updateHomePlatforms, updateHotelId, updateLevel } from "store/slices/account/accountSlice";

import {handleExportClick} from "./utils/exportPdf";
import DmpIcons from "../../components/DmpIcons/icons";
import DashboardFilterMenu from "components/DashboardFilterMenu/DashboardFilterMenu";
import { DashboardDestinationAdsOptions } from "models/DashboardChanneFilters/DestinationAdsChannelOptions";
import { OtherChannelOptions } from "models/DashboardChanneFilters/OtherChannelOptions";
import { DashboardMetaseachOptions } from "models/DashboardChanneFilters/MetasearchChannelOptions";

const NewHomepage = () => {
  const { idaccount } = useParams();
  const dispatch = useDispatch();
  const { calender } = useSelector((state) => state);
  const hotelButtonRef = useRef(null);
  const platformButtonRef = useRef(null);
  const {hotel_id = [], group_id = [], level , hotelList = [], groupList = [], can_use_groups: showGroups, home_platforms: selected_channels = []} = useSelector(state => state?.account)
  const accountName = useSelector(state => state?.account?.account?.account_name);
  const [openHotelModal, setOpenHotelModal] = useState(false);
  const [openPlatformMenu,setOpenPlatformMenu] = useState(false);
  const [selectedHotelOrGroupType, setSelectedHotelOrGroupType] =
    useState("hotels");

  const [deviceIdChannel, setDeviceIdChannel] = useState("GHA_META");
  const [topHotelsIdChannel, setTopHotelsIdChannel] = useState("GHA_META");
  const [topMarketsIdChannel, setTopMarketsIdChannel] = useState("GHA_META");
  const [isChartCollapsed, setIsChartCollapsed] = useState(false);

  // Export PDF
  const targetRef = useRef();
  const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
      if (group_id?.length || hotel_id?.length) {
        if (group_id?.length) {
          dispatch(updateLevel("GROUP"));
        } else {
          dispatch(updateLevel("HOTEL"));
        }
      }
    }, [group_id, hotel_id, dispatch]);

    useEffect(() => {
      if (!selected_channels.length) {
        const allOptions = [
          ...(DashboardMetaseachOptions || []),
          ...(DashboardDestinationAdsOptions || []),
          ...(OtherChannelOptions || []),
        ].map((option) => ({
          ...option,
          isSelected: true,
        }));

        dispatch(updateHomePlatforms(allOptions));
      }
    }, [selected_channels, dispatch]);


  const handleDeviceChange = (event) => {
    const newChannel = event.target.value;
    setDeviceIdChannel(newChannel);
    handleDropdownChange(event, "device");
  };

  const handleTopHotelsChange = (event) => {
    const newChannel = event.target.value;
    setTopHotelsIdChannel(newChannel);
    handleDropdownChange(event, "topHotels");
  };

  const handleTopMarketsChange = (event) => {
    const newChannel = event.target.value;
    setTopMarketsIdChannel(newChannel);
    handleDropdownChange(event, "topMarkets");
  };

  const hotelIdsOrGroupIds = useMemo(() => {
    if(level === "HOTEL"){
      return Array.from(hotel_id || []).filter(id => hotelList.map((h) => h?.["hotel_id"])?.includes(id) && String(hotelList?.[0]?.account_id) === String(idaccount))
    } else {
      return Array.from(group_id || []).filter(id => groupList.map((h) => h?.["id"])?.includes(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[level, JSON.stringify(hotel_id),JSON.stringify(group_id), JSON.stringify(hotelList), JSON.stringify(groupList), idaccount])

  const {
    metrics,
    reportingMetrics,
    chartsMetrics,
    deviceMetrics,
    topHotelsMetrics,
    topMarketsMetrics,
    errorsMessage,
    handleDropdownChange,
    fetchData,
  } = useFetchMetricsData(idaccount, calender, hotelIdsOrGroupIds, level,selected_channels.map(s => s.channel_id));

  useEffect(() => {
    if (calender?.currentDateRange?.startDate && calender?.currentDateRange?.endDate) {
      fetchData();
    }
  }, [
    calender.currentDateRange.startDate,
    calender.currentDateRange.endDate,
    calender.comparitionDateRange.startDate,
    calender.comparitionDateRange.endDate,
    idaccount,
    fetchData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hotelIdsOrGroupIds)
  ]);

  const isExportDisabled =
      metrics.length === 0 ||
      reportingMetrics.length === 0 ||
      chartsMetrics.length === 0 ||
      deviceMetrics.length === 0 ||
      topHotelsMetrics.length === 0 ||
      topMarketsMetrics.length === 0;

  return (
    <DefaultLayout
      ref={targetRef}
      mainBoxSx={{
        display: "flex",
        minHeight: "100vh",
        overflow: "visible",
      }}
      contentBoxSx={{
        backgroundColor: "#FAFAFA",
        flex: 1,
        paddingTop: 2,
        width: "calc(100vw - 180px)",
        height: "auto",
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginRight: "18px",
          marginLeft: "2px",
        }}
      >
        <PageTitles pageTitle="Digital Marketing Platform" />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Tooltip
            title={isExportDisabled ? "Loading data" : "Export to PDF"}
            arrow
          >
            <span
              style={{
                cursor: isExportDisabled ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                filter: isExportDisabled
                  ? "grayscale(100%) brightness(80%)"
                  : "none",
                opacity: isExportDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (!isExportDisabled) {
                  handleExportClick({
                    targetRef,
                    calender,
                    accountName,
                    setIsExporting,
                  });
                }
              }}
            >
              <img src={DmpIcons.ActionsIcons.ExportIcon} alt="Export" />
            </span>
          </Tooltip>
          <DatePicker />
        </Box>
      </Box>
      <Box
        paddingLeft={2}
        sx={{
          display: "flex",
          alignItems: "center",
          color: "#000000",
          fontWeight: "400",
          marginTop: "4px",
          gap: "12px",
        }}
      >
        <div
          onClick={() => {
            setOpenHotelModal(true);
          }}
          ref={hotelButtonRef}
        >
          <Chip
            label={
              level === "HOTEL"
                ? hotel_id.length === 0
                  ? "Hotels"
                  : hotel_id.length === hotelList.length
                  ? "All Hotels"
                  : hotel_id.length === 1
                  ? hotelList?.find((hotel) => hotel.hotel_id === hotel_id?.[0])
                      ?.hotel_name
                  : `${hotel_id.length} ${"hotels"} selected`
                : group_id.length === 0
                ? "Groups"
                : group_id.length === groupList.length
                ? "All Groups"
                : group_id.length === 1
                ? groupList?.find((group) => group.id === group_id?.[0])?.name
                : `${group_id.length} ${"groups"} selected`
            }
            sx={{
              backgroundColor: "inherit",
              fontFamily: "inter",
              fontSize: "12px",
              fontWeight: "400",
              marginRight: "4px",
              height: "26px",
              minWidth: "120px",
              border: "1px solid #BAB7B7",
              ":hover": {
                cursor: "pointer",
                border: "1px solid #000000",
              },
            }}
          />
        </div>
        {openHotelModal && (
          <HotelsFilterMenu
            allHotels={hotelList?.map((ho) =>
              hotel_id.find((hf) => hf === ho.hotel_id)
                ? { ...ho, isSelected: true }
                : { ...ho, isSelected: false }
            )}
            showGroups={showGroups}
            anchorEl={hotelButtonRef.current}
            open={openHotelModal}
            allGroups={groupList?.map((ho) =>
              group_id.includes(ho.id)
                ? { ...ho, isSelected: true }
                : { ...ho, isSelected: false }
            )}
            isSelectAllRequired
            onApply={(selected) => {
              const type = selected?.length ? selected.filter(s => s?.isSelected).some(s => s?.type === "HOTEL") ? "hotels" : "groups" : selectedHotelOrGroupType;
              dispatch(
                updateLevel(
                  type === "hotels" ? "HOTEL" : "GROUP"
                )
              );
              if (type === "hotels") {
                dispatch(
                  updateHotelId(selected.map((item) => item?.["hotel_id"]))
                );
                dispatch(updateGroupId([]));
              } else {
                dispatch(updateGroupId(selected.map((item) => item?.["id"])));
                dispatch(updateHotelId([]));
              }
            }}
            onClose={() => {
              setOpenHotelModal(false);
            }}
            setSelectedHotelOrGroupType={(type) => {
              setSelectedHotelOrGroupType(type);
            }}
            selectedHotelOrGroupType={level === "HOTEL" ? "hotels" : "groups"}
          />
        )}
        <div
          onClick={() => {
            setOpenPlatformMenu(true);
          }}
          ref={platformButtonRef}
        >
          <Chip
            label={
              selected_channels.length === 0
                ? "Platform"
                : selected_channels.length === 1
                ? selected_channels?.[0]?.label
                : selected_channels.length ===
                  [...DashboardMetaseachOptions, ...DashboardDestinationAdsOptions, ...OtherChannelOptions].length
                ? "All Platforms"
                : `${selected_channels.length} platforms selected`
            }
            sx={{
              backgroundColor: "inherit",
              fontFamily: "inter",
              fontSize: "12px",
              fontWeight: "400",
              marginRight: "4px",
              height: "26px",
              minWidth: "120px",
              border: "1px solid #BAB7B7",
              ":hover": {
                cursor: "pointer",
                border: "1px solid #000000",
              },
            }}
          />
        </div>
        {openPlatformMenu && (
          <DashboardFilterMenu
            anchorEl={platformButtonRef.current}
            open={openPlatformMenu}
            options={DashboardMetaseachOptions?.map((po) => ({
              ...po,
              isSelected: selected_channels.find(s => s.id === po.id) ? true : false,
            }))}
            optionsDesignation={DashboardDestinationAdsOptions?.map((po) => ({
              ...po,
            isSelected: selected_channels.find(s => s.id === po.id) ? true : false,
            }))}
            otherAds={OtherChannelOptions?.map((po) => ({
              ...po,
              isSelected: selected_channels.find(s => s.id === po.id) ? true : false,
            }))}
            filterType={"Platform"}
            selectAllTitle={"Platforms"}
            onClose={() => setOpenPlatformMenu(false)}
            onApply={(selected) => {
              dispatch(updateHomePlatforms(selected));
            }}
          />
        )}
      </Box>

      <Box
        sx={{ backgroundColor: "#FAFAFA", pt: "14px", paddingInline: "18px" }}
      >
        <MetricsComponent
          metrics={metrics}
          calender={calender}
          errorMessage={errorsMessage.kpi}
        />

        <Grid container spacing="14px" sx={{ pt: "14px" }}>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <ReportingMetricsTable
              metrics={reportingMetrics}
              errorMessage={errorsMessage.reporting}
            />
          </Grid>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {!isChartCollapsed && (
                <ChartComponent
                  menuOptions={chartsMetrics.metrics}
                  comparitionData={chartsMetrics.compareChartData}
                  rawChartData={chartsMetrics.chartData}
                  axisData={chartsMetrics.chartAxisDate}
                  errorMessage={errorsMessage.charts}
                />
              )}
              <TableContainer
                setIsChartCollapsed={setIsChartCollapsed}
                isChartCollapsed={isChartCollapsed}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing="14px" sx={{ pb: 5, paddingTop: "14px" }}>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <DeviceComponent
              selectedOption={deviceIdChannel}
              handleDropdownChange={handleDeviceChange}
              deviceMetrics={deviceMetrics}
              errorMessage={errorsMessage.device}
            />
            <TopHotelsComponent
              selectedOption={topHotelsIdChannel}
              handleDropdownChange={handleTopHotelsChange}
              topHotelsMetrics={topHotelsMetrics}
              errorMessage={errorsMessage.topHotels}
            />
          </Grid>
          <Grid item lg={6} md={12} sm={12} xs={12}>
            <TopMarketsComponent
              selectedOption={topMarketsIdChannel}
              handleDropdownChange={handleTopMarketsChange}
              topMarketsMetrics={topMarketsMetrics}
              errorMessage={errorsMessage.topMarkets}
            />
          </Grid>
        </Grid>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isExporting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DefaultLayout>
  );
};

export default NewHomepage;